/**
 * Agent API client — HTTP wrapper for agent callable methods.
 * Uses the same userId as the WebSocket connection.
 */

const API_BASE =
	(import.meta.env.VITE_AGENT_HTTP_URL as string | undefined) ??
	"https://boring-agent.codyboring.workers.dev";

function getUserId(): string {
	return localStorage.getItem("boring-agent-user") || "";
}

const PASSCODE_KEY = "boring-agent-passcode";

export function getPasscode(): string {
	if (typeof localStorage === "undefined") return "";
	return localStorage.getItem(PASSCODE_KEY) || "";
}

export function setPasscode(value: string) {
	if (typeof localStorage === "undefined") return;
	localStorage.setItem(PASSCODE_KEY, value);
}

export function clearPasscode() {
	if (typeof localStorage === "undefined") return;
	localStorage.removeItem(PASSCODE_KEY);
}

export async function checkAuth(passcode?: string): Promise<{ ok: boolean; gated: boolean }> {
	const headers: Record<string, string> = {};
	const code = passcode ?? getPasscode();
	if (code) headers.authorization = `Bearer ${code}`;
	const res = await fetch(`${API_BASE}/api/auth/check`, { method: "POST", headers });
	const body = (await res.json().catch(() => ({}))) as { ok?: boolean; gated?: boolean };
	return { ok: Boolean(body.ok), gated: Boolean(body.gated) };
}

export function authHeaders(): Record<string, string> {
	const code = getPasscode();
	return code ? { authorization: `Bearer ${code}` } : {};
}

export const HTTP_BASE = API_BASE;

async function api<T>(path: string, init?: RequestInit): Promise<T> {
	const userId = getUserId();
	if (!userId) throw new Error("No userId");
	const res = await fetch(`${API_BASE}/api/agent/${userId}${path}`, {
		headers: { "Content-Type": "application/json", ...authHeaders() },
		...init,
	});
	if (res.status === 401) {
		clearPasscode();
		window.dispatchEvent(new CustomEvent("agent-auth-required"));
		throw new Error("Unauthorized");
	}
	if (!res.ok) {
		const err = (await res.json().catch(() => ({ error: res.statusText }))) as { error?: string };
		throw new Error(err.error || res.statusText);
	}
	return res.json() as Promise<T>;
}

/* ------------------------------------------------------------------ */
/*  Workspace                                                           */
/* ------------------------------------------------------------------ */

export interface WorkspaceEntry {
	name: string;
	type: "file" | "directory";
	size?: number;
	children?: WorkspaceEntry[];
}

export async function getWorkspaceTree(path = "/"): Promise<WorkspaceEntry[]> {
	return api<WorkspaceEntry[]>(`/workspace/tree?path=${encodeURIComponent(path)}`);
}

export async function readFile(path: string): Promise<{ path: string; content: string | null }> {
	return api<{ path: string; content: string | null }>(
		`/workspace/file?path=${encodeURIComponent(path)}`
	);
}

export async function writeFile(
	path: string,
	content: string
): Promise<{ path: string; bytesWritten: number }> {
	return api<{ path: string; bytesWritten: number }>("/workspace/file", {
		method: "POST",
		body: JSON.stringify({ path, content }),
	});
}

export async function deleteFile(path: string): Promise<{ path: string; deleted: boolean }> {
	return api<{ path: string; deleted: boolean }>(
		`/workspace/file?path=${encodeURIComponent(path)}`,
		{ method: "DELETE" }
	);
}

export async function mkdir(path: string): Promise<{ path: string; created: boolean }> {
	return api<{ path: string; created: boolean }>("/workspace/mkdir", {
		method: "POST",
		body: JSON.stringify({ path }),
	});
}

/* ------------------------------------------------------------------ */
/*  Git                                                                 */
/* ------------------------------------------------------------------ */

export async function gitStatus(): Promise<unknown> {
	return api<unknown>("/git/status");
}

export async function gitInit(defaultBranch?: string): Promise<unknown> {
	return api<unknown>("/git/init", {
		method: "POST",
		body: JSON.stringify({ defaultBranch }),
	});
}

export async function gitAdd(filepath: string): Promise<unknown> {
	return api<unknown>("/git/add", {
		method: "POST",
		body: JSON.stringify({ filepath }),
	});
}

export async function gitCommit(
	message: string,
	authorName?: string,
	authorEmail?: string
): Promise<unknown> {
	return api<unknown>("/git/commit", {
		method: "POST",
		body: JSON.stringify({ message, authorName, authorEmail }),
	});
}

export async function gitLog(depth = 20): Promise<unknown> {
	return api<unknown>(`/git/log?depth=${depth}`);
}

export async function gitDiff(): Promise<unknown> {
	return api<unknown>("/git/diff");
}

/* ------------------------------------------------------------------ */
/*  Meta                                                                */
/* ------------------------------------------------------------------ */

export interface ConversationMeta {
	messageCount: number;
	model: string;
}

export async function getConversationMeta(): Promise<ConversationMeta> {
	return api<ConversationMeta>("/meta");
}

/* ------------------------------------------------------------------ */
/*  Code execution                                                      */
/* ------------------------------------------------------------------ */

export async function runCode(code: string): Promise<unknown> {
	return api<unknown>("/run-code", {
		method: "POST",
		body: JSON.stringify({ code }),
	});
}

/* ------------------------------------------------------------------ */
/*  Conversations                                                       */
/* ------------------------------------------------------------------ */

export interface Conversation {
	id: string;
	title: string;
	createdAt: number;
	updatedAt: number;
	messageCount: number;
}

export async function listConversations(): Promise<Conversation[]> {
	return api<Conversation[]>("/conversations");
}

export async function saveConversation(
	id: string,
	title: string
): Promise<{ id: string; title: string; saved: boolean }> {
	return api<{ id: string; title: string; saved: boolean }>("/conversations", {
		method: "POST",
		body: JSON.stringify({ id, title }),
	});
}

export interface LoadedConversation {
	id: string;
	loaded: boolean;
	title: string;
	messageCount: number;
	messages: Array<{
		id: string;
		role: "user" | "assistant";
		content: string;
		parts?: Array<{ type: string; text?: string }>;
		timestamp: number;
	}>;
}

export async function loadConversation(id: string): Promise<LoadedConversation> {
	return api<LoadedConversation>("/conversations/load", {
		method: "POST",
		body: JSON.stringify({ id }),
	});
}

export async function deleteConversation(id: string): Promise<{ id: string; deleted: boolean }> {
	return api<{ id: string; deleted: boolean }>("/conversations", {
		method: "DELETE",
		body: JSON.stringify({ id }),
	});
}

export async function createNewConversation(): Promise<{ created: boolean }> {
	return api<{ created: boolean }>("/conversations/new", { method: "POST" });
}

export interface Usage {
	day: string;
	messagesToday: number;
	dailyLimit: number;
}

export async function getUsage(): Promise<Usage> {
	return api<Usage>("/usage");
}
